/**
 * Read links for trust.homepal.se.
 *
 * A link gives its recipient access to the documents marked as gated in the
 * trust center. The link itself is minted by the trust stack and returned
 * once. All that is kept here is who it was issued to and how long it lasts.
 */
import React, { useEffect, useMemo, useState } from 'react'

import * as TrustLinksApi from 'redux/api/internal/TrustLinks'
import { useSnackbarActions } from 'redux/actions/Snackbar'

import { SnackbarMessage } from 'types/GlobalSnackbar'
import { CreatedTrustLink, TrustLink } from 'types/GlobalTrustLink'

import Alert from 'components_new/atoms/Alert'
import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Chip from 'components_new/atoms/Chip'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Stack from 'components_new/atoms/Stack'
import Text from 'components_new/atoms/Text'
import TextField from 'components_new/atoms/TextField'
import Tooltip from 'components_new/atoms/Tooltip'

import GenericListItem from 'components_new/molecules/GenericListItem'
import SimpleDialog from 'components_new/molecules/SimpleDialog'
import SimpleTable from 'components_new/molecules/SimpleTable'

import PageLayout from 'components_new/templates/PageLayout'

const DEFAULT_DAYS = 30

const INIT_FORM = {
  name: '',
  company: '',
  days: String(DEFAULT_DAYS),
  email: ''
}

const formatDate = (value: string) => (value ? value.slice(0, 10) : '–')

const statusOf = (link: TrustLink) => {
  if (link.active) return { label: 'Aktiv', color: 'success' as const }
  if (link.status === 'revoked') {
    return { label: 'Återkallad', color: 'default' as const }
  }

  return { label: 'Utgången', color: 'default' as const }
}

const TrustLinksPage = () => {
  const { toggleSnackbarOpen } = useSnackbarActions()

  const [links, setLinks] = useState<TrustLink[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [formOpen, setFormOpen] = useState(false)
  const [form, setForm] = useState(INIT_FORM)
  const [creating, setCreating] = useState(false)
  const [created, setCreated] = useState<CreatedTrustLink | null>(null)
  const [toRevoke, setToRevoke] = useState<TrustLink | null>(null)

  const load = () => {
    setLoading(true)
    TrustLinksApi.getTrustLinks()
      .then((res) => {
        setLinks(res.data.data)
        setError(null)
      })
      .catch(() => setError('Kunde inte hämta länkarna.'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
  }, [])

  const days = Number(form.days)

  const formValid
    = form.name.trim().length > 0
    && form.company.trim().length > 0
    && Number.isFinite(days)
    && days >= 1
    && days <= 365

  const copy = (value: string) => {
    navigator.clipboard.writeText(value)
    toggleSnackbarOpen(SnackbarMessage.CopiedToClipboard)
  }

  const handleCreate = () => {
    setCreating(true)
    TrustLinksApi.createTrustLink({
      name: form.name.trim(),
      company: form.company.trim(),
      days,
      email: form.email.trim() || undefined
    })
      .then((res) => {
        setCreated(res.data.data)
        setFormOpen(false)
        setForm(INIT_FORM)
        load()
      })
      .catch(() => setError('Kunde inte skapa länken.'))
      .finally(() => setCreating(false))
  }

  const handleRevoke = () => {
    if (!toRevoke) return

    const { id } = toRevoke

    setToRevoke(null)
    TrustLinksApi.revokeTrustLink(id)
      .then(() => load())
      .catch(() => setError('Kunde inte återkalla länken.'))
  }

  const rows = useMemo(
    () =>
      links.map((link) => {
        const status = statusOf(link)

        return {
          id: link.id,
          cells: [
            <GenericListItem
              key="recipient"
              component="div"
              disablePadding={true}
              title={link.company}
              body={link.name}
            />,
            <Text key="grantedBy" variant="body2">
              {link.grantedBy || '–'}
            </Text>,
            <Text key="grantedAt" variant="body2">
              {formatDate(link.grantedAt)}
            </Text>,
            <Text key="expiresAt" variant="body2">
              {formatDate(link.expiresAt)}
            </Text>,
            <Chip
              key="status"
              label={status.label}
              color={status.color}
              size="small"
            />,
            <Stack
              key="actions"
              direction="row"
              sx={{ gap: 0.5, justifyContent: 'flex-end' }}
            >
              {link.active
                ? (
                    <Tooltip title="Återkalla">
                      <IconButton onClick={() => setToRevoke(link)}>
                        <Icon name="RemoveCircleOutlineOutlined" />
                      </IconButton>
                    </Tooltip>
                  )
                : null}
            </Stack>
          ]
        }
      }),
    [links]
  )

  return (
    <PageLayout
      content={{ disablePadding: true, fullWidth: true }}
      variant="static"
      topbar={{
        breadcrumbs: [{ title: 'Admin' }, { title: 'Läslänkar' }],
        actions: (
          <Button
            startIcon={<Icon name="Add" />}
            onClick={() => setFormOpen(true)}
            variant="text"
          >
            Skapa länk
          </Button>
        )
      }}
    >
      <Box sx={{ p: 4 }}>
        <Text color="text.secondary" gutterBottom={true} variant="body2">
          Länkarna ger åtkomst till säkerhetsdokumenten på trust.homepal.se.
          Länken visas en enda gång, när den skapas.
        </Text>

        {error
          ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )
          : null}

        <SimpleTable
          hover={false}
          loading={loading}
          columns={[
            { title: 'Mottagare' },
            { title: 'Utfärdad av', width: 220 },
            { title: 'Utfärdad', width: 120 },
            { title: 'Giltig till', width: 120 },
            { title: 'Status', width: 130 },
            { title: '', width: 80, align: 'right' }
          ]}
          rows={rows}
        />
      </Box>

      <SimpleDialog
        actions={
          <>
            <Button variant="text" onClick={() => setFormOpen(false)}>
              Avbryt
            </Button>
            <Button disabled={!formValid || creating} onClick={handleCreate}>
              Skapa
            </Button>
          </>
        }
        onClose={() => setFormOpen(false)}
        open={formOpen}
        title="Skapa läslänk"
      >
        <Stack sx={{ gap: 2, pt: 1 }}>
          <TextField
            label="Namn"
            onChange={(event) =>
              setForm({ ...form, name: event.target.value })
            }
            required={true}
            size="small"
            value={form.name}
          />
          <TextField
            label="Organisation"
            onChange={(event) =>
              setForm({ ...form, company: event.target.value })
            }
            required={true}
            size="small"
            value={form.company}
          />
          <TextField
            helperText="1–365 dagar"
            label="Giltighet i dagar"
            onChange={(event) =>
              setForm({ ...form, days: event.target.value })
            }
            size="small"
            type="number"
            value={form.days}
          />
          <TextField
            helperText="Valfritt. Sparas för att kunna spåra läsningar."
            label="E-post"
            onChange={(event) =>
              setForm({ ...form, email: event.target.value })
            }
            size="small"
            value={form.email}
          />
        </Stack>
      </SimpleDialog>

      <SimpleDialog
        actions={
          <Button onClick={() => setCreated(null)}>Klar</Button>
        }
        onClose={() => setCreated(null)}
        open={!!created}
        title="Länken är skapad"
      >
        <Alert severity="warning" sx={{ mb: 2 }}>
          Kopiera länken nu. Den går inte att visa igen — en tappad länk får
          skapas om.
        </Alert>
        <Text gutterBottom={true} variant="body2">
          {created?.name}, {created?.company}. Gäller till{' '}
          {formatDate(created?.expiresAt ?? '')}.
        </Text>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
          <TextField
            fullWidth={true}
            InputProps={{ readOnly: true }}
            size="small"
            value={created?.url ?? ''}
          />
          <Tooltip title="Kopiera">
            <IconButton onClick={() => copy(created?.url ?? '')}>
              <Icon name="Copy" />
            </IconButton>
          </Tooltip>
        </Stack>
      </SimpleDialog>

      <SimpleDialog
        actions={
          <>
            <Button variant="text" onClick={() => setToRevoke(null)}>
              Avbryt
            </Button>
            <Button color="error" onClick={handleRevoke}>
              Återkalla
            </Button>
          </>
        }
        onClose={() => setToRevoke(null)}
        open={!!toRevoke}
        title="Återkalla länken?"
      >
        <Text variant="body2">
          Länken till {toRevoke?.name}, {toRevoke?.company} slutar fungera vid
          nästa klick. Åtgärden går inte att ångra.
        </Text>
      </SimpleDialog>
    </PageLayout>
  )
}

export default TrustLinksPage
